import React from 'react';
import {
  Box,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

const MethodologySection = ({ primary = '#0A68FF' }) => {
  const pDark = darken(primary, 0.35);
  const pMid = darken(primary, 0.15);
  const pLite = lighten(primary, 0.28);
  const ink = '#0f172a';

  const paragraphs = [
    'We begin each dark web monitoring engagement by confirming the approved scope and identifying the domains, email addresses, and organizational identities that should be monitored. The assessment team validates the target set, normalizes those identifiers, and establishes the baseline that will be used to distinguish meaningful exposure from noise or unrelated third-party records.',
    'Once the scope is defined, we query curated leak sources and indexed datasets to identify records tied to the monitored organization. We collect exposed records, normalize fields across sources, and correlate repeated identities so we can understand whether a single account appears once or is repeatedly exposed across multiple leak repositories.',
    'After collection, we enrich the results with breach intelligence to determine which verified breach databases are associated with the exposed identities, when those breaches occurred, and what categories of data were disclosed. This allows the assessment team to evaluate both recency and likely impact while separating raw leak volume from validated breach events.',
    'We then review the resulting exposure patterns, document the identities and datasets most relevant to risk, and translate those results into reporting for both leadership and technical stakeholders. The goal is to provide a practical understanding of observed exposure, likely account risk, and the monitoring-driven actions that should be prioritized next.',
  ];

  return (
    <Box
      id="methodology"
      sx={{
        maxWidth: '8.5in',
        p: '0.72in 0.82in',
        bgcolor: '#fff',
        color: ink,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      <Stack spacing={1.1} sx={{ mb: 1.4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
          Methodology
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: alpha(ink, 0.8),
            lineHeight: 1.6,
            maxWidth: '92%',
          }}
        >
          This section explains how we normally conduct dark web monitoring from scoped identity review through enrichment, exposure analysis, and reporting.
        </Typography>
      </Stack>

      <Divider
        sx={{
          mb: 2.1,
          borderColor: alpha(ink, 0.08),
          '&::after': {
            content: '""',
            display: 'block',
            height: 4,
            width: 180,
            mt: 1,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${pDark}, ${pMid} 60%, ${pLite})`,
          },
          '@media print': { '&::after': { background: pMid } },
        }}
      />

      <Box
        sx={{
          mb: 1.8,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: alpha(primary, 0.045),
          border: `1px solid ${alpha(primary, 0.14)}`,
        }}
      >
        <Typography variant="body1" sx={{ color: alpha(ink, 0.86), lineHeight: 1.6 }}>
          We use a staged methodology so the monitoring results progress from scoped identity collection into validated breach correlation and practical reporting.
        </Typography>
      </Box>

      <Stack spacing={1.45}>
        {paragraphs.map((paragraph) => (
          <Typography
            key={paragraph}
            variant="body1"
            sx={{
              color: alpha(ink, 0.96),
              lineHeight: 1.65,
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

export default MethodologySection;
